import type { DataStateType } from '#components/utils/dataState.type';
import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useFiltersForQueryKey } from '#composables/useFiltersForQueryKey';
import { useLanguage } from '#composables/useLanguage';
import { getDataTableKpi, type DataTableInputType, type SortType } from '#domain/performance/kpi/kpi.query';
import { withLikeForLikeStoreKeys } from '#domain/performance/kpi/prepare-query-input';
import { useLikeForLikePerimeter } from '#domain/performance/likeForLike/useLikeForLikePerimeter';
import type {
  DataTableColumn,
  DataTableDimensionsType,
  DataTableKpiDisplayNameType,
  DataTableViewType
} from '@carrefour-gcs/shared';
import { useInfiniteQuery, type QueryFunctionContext, type QueryKey } from '@tanstack/vue-query';
import { computed, unref, type Ref } from 'vue';
import { formatDataTableRow } from './formatDataTableRow';

interface UseDataTableRowsParams {
  dataTableView: Ref<DataTableViewType>;
  selectedDimensions: Ref<DataTableDimensionsType[] | null>;
  selectedKpis: Ref<DataTableKpiDisplayNameType[] | null>;
  sortQueryParams: Ref<SortType | undefined>;
  columnsDefinition: Ref<Record<string, DataTableColumn>>;
  canFetchRows: Ref<boolean>;
  paginationDefaultValue: number;
  sourceContext?: DataTableInputType['sourceContext'];
  excludeFiltersFromQueryKey?: string[];
  benchmarkQuery?: Ref<DataTableInputType['benchmark']>;
  benchmarkIcon?: Ref<'carrefour' | 'market' | undefined>;
}

export function useDataTableRows(params: UseDataTableRowsParams) {
  const {
    dataTableView,
    selectedDimensions,
    selectedKpis,
    canFetchRows,
    sortQueryParams,
    columnsDefinition,
    paginationDefaultValue,
    sourceContext,
    excludeFiltersFromQueryKey,
    benchmarkQuery,
    benchmarkIcon
  } = params;

  const { t, language } = useLanguage();

  const {
    taxonomy: { taxonomy },
    withTax: { isWithTax },
    filters: { selectedFiltersKpiQuery },
    period: { selectedPeriod, comparisonPeriod },
    storePerimeter: { storePerimeter },
    myDataIncluded: { isMyDataIncluded },
    detentionPeriod: { detentionPeriod }
  } = useKpisPerimeter();

  const excludedFilterKeys = excludeFiltersFromQueryKey ?? [];
  const filtersForQueryKey = useFiltersForQueryKey(selectedFiltersKpiQuery, excludedFilterKeys);
  const { resolveLikeForLikeStoreKeys } = useLikeForLikePerimeter();

  // null (not undefined) so the cache key survives JSON serialization identically with and without benchmark
  const benchmarkForQueryKey = computed(() => benchmarkQuery?.value ?? null);

  const fetchDatatableKpi = async ({ pageParam = 1, signal }: QueryFunctionContext<QueryKey, number>) => {
    if (!sortQueryParams.value) {
      throw new Error('Sort should be set before data fetching');
    }

    // L4L: the front-resolved perimeter is a data dependency of the request.
    const likeForLikeStoreKeys = await resolveLikeForLikeStoreKeys();

    const eligibleDetentionViews = ['possessionZoomPage', 'possessionZoomPageWithTheoretical', 'performance'];

    const res = await getDataTableKpi(
      {
        period: selectedPeriod.value,
        comparisonPeriod: comparisonPeriod.value,
        ...(eligibleDetentionViews.includes(dataTableView.value ?? '') && { detentionPeriod: detentionPeriod.value }),
        query: {
          filters: withLikeForLikeStoreKeys(selectedFiltersKpiQuery.value, storePerimeter.value, likeForLikeStoreKeys),
          isWithTax: isWithTax.value,
          isMyDataIncluded: isMyDataIncluded.value
        },
        pagination: {
          limit: pageParam * paginationDefaultValue
        },
        dimensions: selectedDimensions.value ?? [],
        selectedKpis: selectedKpis.value ?? [],
        sort: unref(sortQueryParams),
        taxonomy: unref(taxonomy),
        storePerimeter: unref(storePerimeter),
        dataTableView: unref(dataTableView),
        sourceContext,
        benchmark: benchmarkQuery?.value,
        language: language.value
      },
      signal
    );

    const isSuccessResponse = res.status === 'success';
    return isSuccessResponse && res.data.lines;
  };

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [
      getDataTableKpi.name,
      selectedPeriod,
      comparisonPeriod,
      selectedDimensions,
      selectedKpis,
      sortQueryParams,
      filtersForQueryKey,
      isWithTax,
      isMyDataIncluded,
      taxonomy,
      storePerimeter,
      dataTableView,
      detentionPeriod,
      benchmarkForQueryKey,
      // Segment rows come back ordered by their translated label, so the order depends on the language.
      language
    ],
    queryFn: fetchDatatableKpi,
    enabled: canFetchRows,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage && lastPage.length < paginationDefaultValue) {
        return undefined;
      }
      return pages.length + 1;
    }
  });

  const dataState = computed((): DataStateType => {
    if (isFetchingNextPage.value) {
      return 'appending';
    }
    if (isLoading.value) {
      return 'loading';
    }
    if (isError.value) {
      return 'error';
    }
    return 'success';
  });

  const getRawRows = () => data.value?.pages[data.value?.pages.length - 1] || [];

  const rows = computed(() => {
    if (!columnsDefinition.value) {
      return [];
    }

    return getRawRows().map((row) =>
      formatDataTableRow({
        row,
        columnsDefinition: columnsDefinition.value,
        t,
        language: language.value,
        dataTableView: dataTableView.value,
        benchmarkIcon: benchmarkIcon?.value
      })
    );
  });

  const isLoadingData = computed(() => ['loading', 'appending'].includes(dataState.value));

  const loadMore = computed(() => {
    return {
      canLoadMore: hasNextPage.value,
      isLoading: isLoadingData.value,
      label: t('commonLabel.loadMore')
    };
  });

  return {
    rows,
    isLoadingData,
    loadMore,
    fetchNextPage: fetchNextPage as () => void
  };
}
